// views/Dashboard/PainelExecutivo/sections/geradoras/SecaoGeradoras.tsx
import React from 'react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import type { FiltrosDashboard } from '../../types';
import { rotuloPeriodoDashboard } from '../../../../../src/lib/dashboard/periodoDashboard';
import { useGeradorasDashboardData } from './useGeradorasDashboardData';
import LoadingSpinner from '../../../../../src/components/LoadingSpinner';
import ComparativoResumo from '../../components/ComparativoResumo';

interface Props {
  filtros: FiltrosDashboard;
}

const fmtKwh = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh`;

const CardGeradoras: React.FC<{ titulo: string; valor: string; subtitulo?: string }> = ({ titulo, valor, subtitulo }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide truncate">{titulo}</p>
    <p className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">{valor}</p>
    {subtitulo && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{subtitulo}</p>}
  </div>
);

const SecaoGeradoras: React.FC<Props> = ({ filtros }) => {
  const { pontos, energiaInjetadaKwh, saldoAcumuladoKwh, loading, erro } = useGeradorasDashboardData(filtros.periodo);
  const { pontos: pontosComparacao, energiaInjetadaKwh: energiaInjetadaComparacao } = useGeradorasDashboardData(filtros.periodoComparacao);

  const totalGerado = pontos.reduce((s, p) => s + p.energiaGeradaKwh, 0);
  const totalEsperado = pontos.reduce((s, p) => s + p.energiaEsperadaKwh, 0);
  const gapPct = totalEsperado > 0 ? (totalGerado / totalEsperado - 1) * 100 : null;
  const totalGeradoComparacao = pontosComparacao.reduce((s, p) => s + p.energiaGeradaKwh, 0);

  if (erro) {
    return <p className="text-sm font-semibold text-red-600 dark:text-red-400">{erro}</p>;
  }

  if (loading && pontos.length === 0) {
    return <LoadingSpinner label="Carregando seção Usinas Geradoras..." />;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-bold text-gray-800 dark:text-white">Usinas Geradoras</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <CardGeradoras
          titulo="Energia Gerada"
          valor={fmtKwh(totalGerado)}
          subtitulo={gapPct !== null ? `${gapPct >= 0 ? '+' : ''}${gapPct.toFixed(1)}% vs. esperado` : undefined}
        />
        <CardGeradoras titulo="Energia Injetada" valor={fmtKwh(energiaInjetadaKwh)} />
        <CardGeradoras titulo="Saldo Acumulado" valor={fmtKwh(saldoAcumuladoKwh)} subtitulo="saldo atual (não filtrado por período)" />
      </div>

      {filtros.periodoComparacao && (
        <ComparativoResumo
          rotuloComparacao={rotuloPeriodoDashboard(filtros.periodoComparacao)}
          itens={[
            { titulo: 'Energia Gerada', valorAtual: totalGerado, valorComparacao: totalGeradoComparacao, formatar: fmtKwh },
            { titulo: 'Energia Injetada', valorAtual: energiaInjetadaKwh, valorComparacao: energiaInjetadaComparacao, formatar: fmtKwh },
          ]}
        />
      )}

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
        <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-1">Energia Gerada — Real vs. Esperado</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Esperado = capacidade instalada × fator de geração (configurável em Config → APIs) × dias do mês</p>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={pontos} margin={{ left: 0, right: 0, top: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
            <XAxis dataKey="mesReferencia" tick={{ fontSize: 10 }} stroke="#9ca3af" />
            <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => fmtKwh(v)} width={90} />
            <Tooltip formatter={(v: number) => fmtKwh(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="energiaGeradaKwh" name="Gerado" fill="#5F6C37" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="energiaEsperadaKwh" name="Esperado" stroke="#F6A937" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SecaoGeradoras;
